import { useTranslation } from 'react-i18next'
import { makeStyles } from '@mui/styles'
import { styled, Theme } from '@mui/material/styles'
import { Typography, IconButton } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNewOutlined'
import Scrollbar from '../../control/Scrollbar'
import Drawer from '../../control/Drawer'

type FormMapLayerHelpProps = {
    onClose: () => void
    isOpen: boolean
}

const FormMapLayerHelp = (props: FormMapLayerHelpProps) => {
    const { onClose, isOpen } = props
    const { t } = useTranslation()
    const classes = useStyles()

    const infoContent = [
        {
            header: t('LABEL.MAP.MAP_LAYER.LAYER_TYPE_LAYER'),
            content: 'บริการแผนที่ รองรับแผนที่แบบรูปภาพ (Image layers) และแผนที่ Tile caches ผ่านทาง ArcGIS Server',
            exampleUrl: 'http://<server name>/arcgis/rest/services/<map name>/MapServer',
        },
        {
            header: t('LABEL.MAP.MAP_LAYER.LAYER_TYPE_WMS'),
            content:
                'บริการแผนที่ตามมาตรฐาน OGC (Open Geospatial Consortium) แบบภาพแผนที่ dynamic map service ผ่านทาง ArcGIS Server',
            exampleUrl:
                'http://<server name>/arcgis/services/<map name>/MapServer/WMSServer?request=GetCapabilities&service=WMS',
        },
        {
            header: t('LABEL.MAP.MAP_LAYER.LAYER_TYPE_WMTS'),
            content:
                'บริการแผนที่ตามมาตรฐาน OGC (Open Geospatial Consortium) แบบภาพแผนที่ tile cached ผ่านทาง ArcGIS Server',
            exampleUrl: 'https://<server name>/arcgis/rest/services/<map name>/MapServer/WMTS',
        },
    ]

    const remark =
        '* ระบบรองรับมาตรฐานความปลอดภัย HTTP Protocol แบบ https เพื่อความปลอดภัยในการใช้งานบริการแผนที่ของท่าน  และเป็นแผนที่ที่เผยแพร่แบบสาธารณะ'

    return (
        <Drawer
            isOpen={isOpen}
            inlineStyle={{ position: 'unset' }}
            hideBackdrop
            overrideClass={{ paper: classes.drawerPaper }}
        >
            <StyledDivContainer>
                <StyledDivTitle>
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{
                            position: 'absolute',
                            left: 10,
                            top: 15,
                        }}
                        className={classes.backIcon}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <StyledTypography variant="body2">{t('LABEL.MAP.MAP_LAYER.HELP.TITLE')}</StyledTypography>
                </StyledDivTitle>
                <StyledContent>
                    <div style={{ width: '100%', height: '100%' }}>
                        <Scrollbar scrollbarTrackBorderRadius={10} scrollbarThumbBorderRadius={10}>
                            <StyledContentGroup>
                                {infoContent.map((item, index) => (
                                    <StyledDivContent key={'mapItem' + index.toString()}>
                                        <StyledTypoSection>
                                           {item.header}
                                        </StyledTypoSection>
                                        <StyledTypoDescription>{item.content}</StyledTypoDescription>
                                        <StyledDivUrlExample>{item.exampleUrl}</StyledDivUrlExample>
                                    </StyledDivContent>
                                ))}
                                <StyledDivContent>
                                    <StyledTypoRemark>{remark}</StyledTypoRemark>
                                </StyledDivContent>
                            </StyledContentGroup>
                        </Scrollbar>
                    </div>
                </StyledContent>
            </StyledDivContainer>
        </Drawer>
    )
}

export default FormMapLayerHelp

const useStyles = makeStyles((theme: Theme) => ({
    drawerPaper: {
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    backIcon: {
        color: `${theme.palette.text.secondary} !important`,
    },
    divButton: {
        paddingTop: '24px',
        position: 'absolute',
        right: 0,
    },

    divider: {
        margin: '20px 0px !important',
    },
    lineClamp: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        '-webkit-line-clamp': 1,
        '-webkit-box-orient': 'vertical',
    },
}))

const StyledDivContainer = styled('div')(({ theme }) => ({
    width: '350px',
    height: '100%',
    [theme.breakpoints.down('sm')]: {
        width: '100%',
    },
}))

const StyledDivTitle = styled('div')(({ theme }) => ({
    width: '100%',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    padding: '20px 0px',
    backgroundColor: theme.palette.secondary.main,
    boxShadow: theme.palette.getContrastText('shadow-bottom'),
}))

const StyledTypography = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    textAlign: 'center',
}))

const StyledContent = styled('div')(({ theme }) => ({
    display: 'flex',
    height: `calc(100% - 63px)`,
}))

const StyledContentGroup = styled('div')(({ theme }) => ({
    margin: '20px',
}))

const StyledDivContent = styled('div')(({ theme }) => ({
    marginTop: '10px',
    marginBottom: '20px',
}))

const StyledTypoSection = styled(Typography)(({ theme }) => ({
    color: theme.palette.secondary.dark,
    fontWeight: 'bold',
}))

const StyledTypoRemark = styled(Typography)(({ theme }) => ({
    color: theme.palette.secondary.dark,
    fontStyle: 'italic',
    fontSize: 14,
}))

const StyledTypoDescription = styled(Typography)(({ theme }) => ({
    color: theme.palette.secondary.dark,
    fontSize: 14,
    marginBottom: 10,
}))

const StyledDivUrlExample = styled(Typography)(({ theme }) => ({
    border: '1px solid #C9C9C9',
    backgroundColor: '#F9FBFF',
    borderRadius: 5,
    padding: 5,
    color: '#9A9A9A',
    fontSize: 14,
}))
